'use client';

import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button, IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Type } from '@repo/design-system/components/ui/type';
import { useReducedMotion } from '@repo/design-system/hooks/useReducedMotion';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { getHost } from '@repo/host/host';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectActiveCategoryId, selectIsMobileViewport, selectSidebarOpen, setActiveCategoryId, setAutoFocusSearch, setSidebarOpen } from '@repo/store/maps/slices/clientContext';
import { type FilterState, selectExternalMapId, selectFilterState, selectLocationCategories, selectSelectedMarkerId, setFilterStateFromUrl } from '@repo/store/maps/slices/mapData';
import { CUSTOM_PINS_CATEGORY_ID, groupCategories } from '@repo/store/maps/utils/mapData';
import { HostType } from '@repo/types/host';
import type { MapInitializationData } from '@repo/types/maps/map-types';
import { useEffect, useMemo, useRef } from 'react';
import { useMobileMapsEmbed } from '../utils/use-mobile-maps-embed';
import { useCategorySwapAnimation } from './hooks/use-category-swap-animation';
import { DOT_SCALE, getPinSelectTintStyles } from './hooks/use-pin-select-tint';
import { useResponsivePills } from './hooks/use-responsive-pills';
const toolbarStyles = css({
  position: 'absolute',
  top: '16',
  left: '16',
  right: '16',
  zIndex: '[13]',
  display: 'flex',
  alignItems: 'center',
  gap: '8',
  overflow: 'hidden',
  '& > *': {
    flexShrink: 0
  }
});
const searchPseudoInputStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: '8',
  backgroundColor: 'white',
  borderRadius: 'full',
  height: '[48px]',
  width: '[172px]',
  paddingX: '16',
  cursor: 'pointer',
  boxShadow: 'lg',
  border: 'none',
  outline: 'none',
  _hover: {
    backgroundColor: 'neutral.100'
  }
});
const mobileToolbarStyles = css({
  position: 'absolute',
  left: 'none',
  right: 'none',
  zIndex: '[13]',
  display: 'flex',
  flexDirection: 'column',
  gap: '8'
});
const mobileCategoryRowStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: '8',
  overflowX: 'auto',
  scrollbarWidth: '[none]',
  paddingX: '16',
  '&::-webkit-scrollbar': {
    display: 'none'
  }
});
const MAX_CATEGORIES_MOBILE = 6;
const MAX_CATEGORIES_DESKTOP = 5;
const TRANSITION_DURATION_MS = 200;
const TOOLBAR_EASING = 'cubic-bezier(0.215, 0.61, 0.355, 1)';
export const MapToolbar = ({
  providedMapData
}: {
  providedMapData: MapInitializationData;
}) => {
  const dispatch = useAppDispatch();
  const {
    t
  } = useT(['game_map']);
  const reduxCategories = useAppSelector(state => state.mapData.groupedCategories);
  const hasCustomPins = useAppSelector(state_0 => state_0.mapData.customPins.length > 0);
  const selectedMarkerId = useAppSelector(selectSelectedMarkerId);
  const locationCategories = useAppSelector(selectLocationCategories);
  const isMobileViewport = useAppSelector(selectIsMobileViewport);
  const sidebarOpen = useAppSelector(selectSidebarOpen);
  const reducedMotion = useReducedMotion();
  const analytics = useAnalytics();
  const externalMapId = useAppSelector(selectExternalMapId);
  const hostType = useHostType();
  const {
    enabled: mobileMapsEmbed,
    toolbarTopOffset: mobileMapsToolbarTopOffset
  } = useMobileMapsEmbed();
  const isOverlay = hostType === HostType.OVERLAY;
  const useDesktopLayout = !isMobileViewport || isOverlay;
  const toolbarRef = useRef<HTMLDivElement>(null);
  const activeCategoryId = useAppSelector(selectActiveCategoryId);
  const filters = useAppSelector(selectFilterState);
  const baselineFiltersRef = useRef<FilterState | null>(null);
  useEffect(() => {
    if (!baselineFiltersRef.current && reduxCategories.length > 0) {
      baselineFiltersRef.current = {
        ...filters,
        hiddenSubcategories: [...filters.hiddenSubcategories]
      };
    }
  }, [reduxCategories.length, filters]);
  const hasCustomFilters = useMemo(() => {
    if (!baselineFiltersRef.current || activeCategoryId) return false;
    const baseline = baselineFiltersRef.current;
    if (filters.hideAll !== baseline.hideAll) return true;
    if (filters.teleportOnly !== baseline.teleportOnly) return true;
    if (filters.unfoundOnly !== baseline.unfoundOnly) return true;
    if (filters.searchTerm !== baseline.searchTerm) return true;
    if (filters.hiddenSubcategories.length !== baseline.hiddenSubcategories.length) return true;
    const baselineSet = new Set(baseline.hiddenSubcategories);
    return filters.hiddenSubcategories.some(id => !baselineSet.has(id));
  }, [filters, activeCategoryId]);
  const categoryButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const defaultCategories = useMemo(() => groupCategories(providedMapData.mapData, {}), [providedMapData.mapData]);
  const categories = reduxCategories?.length ? reduxCategories : defaultCategories;
  const selectedPinCategoryId = selectedMarkerId ? locationCategories[selectedMarkerId]?.externalId ?? null : null;
  useEffect(() => {
    if (!activeCategoryId || categories.length === 0) return;
    const exists = categories.some(c => c.original.visibleByDefault && c.externalId === activeCategoryId);
    if (!exists) {
      dispatch(setActiveCategoryId(null));
    }
  }, [activeCategoryId, categories, dispatch]);
  const maxCategories = useDesktopLayout ? MAX_CATEGORIES_DESKTOP : MAX_CATEGORIES_MOBILE;
  const allCategories = useMemo(() => {
    const filtered = categories.filter(c_0 => c_0.original.visibleByDefault && (c_0.externalId !== CUSTOM_PINS_CATEGORY_ID || hasCustomPins));
    let top = filtered.slice(0, maxCategories);
    if (activeCategoryId && !top.some(c_2 => c_2.externalId === activeCategoryId)) {
      const promoted = filtered.find(c_1 => c_1.externalId === activeCategoryId);
      if (promoted) {
        top = [promoted, ...top];
      }
    }
    if (selectedPinCategoryId && !top.some(c_4 => c_4.externalId === selectedPinCategoryId)) {
      const promoted_0 = filtered.find(c_3 => c_3.externalId === selectedPinCategoryId);
      if (promoted_0) {
        return [promoted_0, ...top];
      }
    }
    return top;
  }, [categories, hasCustomPins, selectedPinCategoryId, activeCategoryId, maxCategories]);
  const displayedCategories = useCategorySwapAnimation({
    allCategories,
    reducedMotion,
    categoryButtonRefs
  });
  const pinTint = getPinSelectTintStyles(selectedPinCategoryId, activeCategoryId, allCategories);
  const visibleCount = useResponsivePills({
    toolbarRef,
    isMobileViewport: !useDesktopLayout,
    displayedCategories,
    activeCategoryId
  });
  const openSidebar = (source: 'toolbar_expand' | 'toolbar_search' | 'toolbar_more') => {
    dispatch(setSidebarOpen(true));
    analytics.event('game_map_resize_sidebar', {
      mapId: externalMapId,
      collapsed: false,
      platform: hostType,
      source
    });
  };
  const handleSearchClick = () => {
    dispatch(setAutoFocusSearch(true));
    if (useDesktopLayout) {
      openSidebar('toolbar_search');
    }
  };
  const handleMoreClick = () => {
    if (!useDesktopLayout) {
      dispatch(setAutoFocusSearch(true));
    } else {
      openSidebar('toolbar_more');
    }
  };
  const handleMobileMapsBackClick = () => {
    void getHost().close('mobile-maps', true);
  };
  const handleCategoryClick = (categoryExternalId: string) => {
    dispatch(setActiveCategoryId(categoryExternalId));
    analytics.event('game_map_toggle_category', {
      mapId: externalMapId,
      categoryId: categoryExternalId,
      filtered: true,
      platform: hostType,
      source: 'toolbar'
    });
  };
  const handleClearFilter = () => {
    const clearedId = activeCategoryId;
    dispatch(setActiveCategoryId(null));
    if (clearedId) {
      analytics.event('game_map_toggle_category', {
        mapId: externalMapId,
        categoryId: clearedId,
        filtered: false,
        platform: hostType,
        source: 'toolbar'
      });
    }
  };
  const handleClearCustomFilters = () => {
    if (baselineFiltersRef.current) {
      dispatch(setFilterStateFromUrl(baselineFiltersRef.current));
    }
  };
  const isActive = activeCategoryId !== null;
  const transitionStyle = reducedMotion ? 'none' : 'background-color 150ms ease, color 150ms ease';
  const dotTransitionStyle = reducedMotion ? 'none' : 'opacity 150ms ease, transform 150ms ease, width 150ms ease, margin-right 150ms ease';
  const closeIconTransitionStyle = reducedMotion ? 'none' : 'opacity 120ms ease';
  const renderCategoryButtons = (useVisibleCount: boolean) => displayedCategories.map((category, index) => {
    const isCategoryActive = activeCategoryId === category.externalId;
    const isHinted = selectedPinCategoryId === category.externalId && !!pinTint;
    const isHidden = isActive && !isCategoryActive || useVisibleCount && index >= visibleCount;
    return <span key={category.externalId} data-category-pill ref={(el: HTMLSpanElement | null) => {
      const btn = el?.firstElementChild as HTMLButtonElement | null;
      if (btn) {
        categoryButtonRefs.current.set(category.externalId, btn);
      } else {
        categoryButtonRefs.current.delete(category.externalId);
      }
    }} className={css({
      display: 'contents'
    })}>
                    <Button style={{
        display: isHidden ? 'none' : 'inline-flex',
        transition: transitionStyle,
        backgroundColor: isCategoryActive ? category.original.color : isHinted ? pinTint.backgroundColor : undefined,
        color: isCategoryActive ? 'white' : undefined
      }} onClick={() => isCategoryActive ? handleClearFilter() : handleCategoryClick(category.externalId)} color="white" size="sm" className={css({
        flexShrink: 0
      })}>
                        <span className={css({
          width: '[6px]',
          height: '[6px]',
          borderRadius: 'full',
          flexShrink: 0,
          overflow: 'hidden'
        })} style={{
          backgroundColor: category.original.color,
          transition: dotTransitionStyle,
          opacity: isCategoryActive ? 0 : 1,
          transform: isCategoryActive ? 'scale(0)' : isHinted ? `scale(${DOT_SCALE})` : undefined,
          width: isCategoryActive ? 0 : '6px',
          marginRight: isCategoryActive ? '-8px' : undefined
        }} />
                        {category.title}
                        <span className={css({
          display: 'flex',
          alignItems: 'center'
        })} style={{
          transition: closeIconTransitionStyle,
          opacity: isCategoryActive ? 1 : 0,
          pointerEvents: isCategoryActive ? 'auto' : 'none',
          position: isCategoryActive ? 'relative' : 'absolute',
          width: isCategoryActive ? 'auto' : 0,
          overflow: 'hidden'
        }}>
                            <Icon icon="close" size="sm" decorative />
                        </span>
                    </Button>
                </span>;
  });
  const moreButton = <Button data-toolbar-more style={{
    display: isActive ? 'none' : 'inline-flex'
  }} onClick={handleMoreClick} color="white" size="sm">
            More
        </Button>;
  const toolbarVisibilityStyles = css({
    opacity: sidebarOpen ? 0 : 1,
    pointerEvents: sidebarOpen ? 'none' : 'auto',
    visibility: sidebarOpen ? 'hidden' : 'visible',
    transition: reducedMotion ? '[none]' as const : `[opacity ${TRANSITION_DURATION_MS}ms ${TOOLBAR_EASING}, visibility ${TRANSITION_DURATION_MS}ms ${TOOLBAR_EASING}]` as const
  });
  if (!useDesktopLayout) {
    const mobileToolbarTop = mobileMapsEmbed ? `calc(max(18px, env(safe-area-inset-top, 0px)) + ${mobileMapsToolbarTopOffset}px)` : 'calc(16px + env(safe-area-inset-top, 0px))';
    return <div ref={toolbarRef} className={mobileToolbarStyles} style={{
      top: mobileToolbarTop,
      zIndex: mobileMapsEmbed ? 40 : undefined
    }}>
                <div className={mobileCategoryRowStyles}>
                    {mobileMapsEmbed && <Button type="button" onClick={handleMobileMapsBackClick} color="white" size="sm" aria-label={t($ => $.back_to_all_maps)} className={css({
          flexShrink: 0
        })}>
                            <Icon icon="arrow_back" decorative />
                            {t($_0 => $_0.all_maps)}
                        </Button>}
                    {hasCustomFilters && !activeCategoryId && <span className={css({
          display: 'contents'
        })}>
                            <Button variant="ghost" onClick={() => dispatch(setAutoFocusSearch(true))} color="primary" size="sm" className={css({
            flexShrink: 0,
            backgroundColor: 'blackAlpha.20',
            color: 'white',
            backdropFilter: '[blur(24px)]'
          })}>
                                Filtered
                                <Icon icon="close" decorative className={css({
              fontSize: '[12px]',
              color: 'white'
            })} onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleClearCustomFilters();
            }} />
                            </Button>
                        </span>}
                    {renderCategoryButtons(false)}
                    {moreButton}
                </div>
            </div>;
  }
  return <div ref={toolbarRef} className={`${toolbarStyles} ${toolbarVisibilityStyles}`}>
            <IconButton data-toolbar-expand onClick={() => openSidebar('toolbar_expand')} color="white" size="lg" aria-label={t($_1 => $_1.expand_sidebar)}>
                <Icon icon="left_panel_open" decorative />
            </IconButton>

            <button type="button" data-toolbar-search className={searchPseudoInputStyles} onClick={handleSearchClick}>
                <Icon icon="search" size="md" decorative className={css({
        color: 'neutral.600'
      })} />
                <Type variant="body-s" color="neutral.500">
                    Search
                </Type>
            </button>

            {renderCategoryButtons(true)}
            {moreButton}
        </div>;
};